import React from 'react'
import { Button, Input, Card, Switch, Row, Col } from 'antd'
import { QrCodeFormData, QrCodeState } from 'Types/QrCode.d'
import OLLogo from 'Assets/images/onelife_logo.png'

interface QrCodeOptionsProps {
  formData: QrCodeFormData
  state: QrCodeState
  onFormDataChange: (updates: Partial<QrCodeFormData>) => void
  onGenerateQRCodes: () => void
}

const QrCodeOptions: React.FC<QrCodeOptionsProps> = ({
  formData,
  state,
  onFormDataChange,
  onGenerateQRCodes
}) => {
  if (!state.sheetData.length) return null

  return (
    <Card 
      title={<span className="text-lg font-semibold">Step 2: Configure QR Code Options</span>} 
      bordered={false} 
      className="mt-6 shadow-md hover:shadow-lg transition-shadow"
    >
      <div className="p-4 w-full">
        <div className="flex flex-col gap-6">
          {/* URL Shortener (Kutt) */}
          <div className="border-b pb-4">
            <h3 className="text-md font-medium mb-3 text-gray-700">URL Shortener (Kutt)</h3>
            <Row gutter={16} align="middle">
              <Col span={6}>
                <div className="flex items-center">
                  <Switch 
                    checked={formData.useKutt} 
                    onChange={(checked) => onFormDataChange({ useKutt: checked })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Use Kutt URL Shortener
                  </label>
                </div>
              </Col>
            </Row>
            <div className="text-xs text-gray-500 mt-2">
              {formData.useKutt ? (
                <p>URLs will be shortened before generating QR codes.</p>
              ) : (
                <p>Enable to shorten URLs with Kutt before generating QR codes.</p>
              )}
            </div>
          </div>

          {/* QR Code Detail Level Controls */}
          <div className="border-b pb-4">
            <h3 className="text-md font-medium mb-3 text-gray-700">QR Code Detail Level</h3>
            <Row gutter={16}>
              <Col span={12}>
                <label className="text-sm font-medium text-gray-700 block mb-1">Error Correction Level</label>
                <select
                  value={formData.errorCorrectionLevel}
                  onChange={(e) => onFormDataChange({ errorCorrectionLevel: e.target.value as 'L' | 'M' | 'Q' | 'H' })}
                  className="w-full border-2 focus:border-blue-400 rounded p-2"
                >
                  <option value="L">Low (7% recovery)</option>
                  <option value="M">Medium (15% recovery)</option>
                  <option value="Q">Quartile (25% recovery)</option>
                  <option value="H">High (30% recovery)</option>
                </select>
                <div className="text-xs text-gray-500 mt-1">
                  Lower correction = less detail but less error tolerance
                </div>
              </Col>
              <Col span={12}>
                <label className="text-sm font-medium text-gray-700 block mb-1">QR Size (px)</label>
                <Input
                  type="number"
                  min={600}
                  max={3000}
                  step={300}
                  value={formData.qrWidth}
                  onChange={(e) => onFormDataChange({ qrWidth: Number(e.target.value) })}
                  className="border-2 focus:border-blue-400"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Size of the generated QR code image
                </div>
              </Col>
            </Row>
          </div>

          {/* Logo Controls */}
          <div className="border-b pb-4">
            <h3 className="text-md font-medium mb-3 text-gray-700">Logo Options</h3>
            <Row gutter={16}>
              <Col span={8}>
                <div className="flex items-center mb-3">
                  <Switch 
                    checked={formData.useDefaultLogo} 
                    onChange={(checked) => onFormDataChange({ useDefaultLogo: checked })}
                    className="mr-2"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Use Default OneLife Logo
                  </label>
                </div>
                {!formData.useDefaultLogo && (
                  <>
                    <label className="text-sm font-medium text-gray-700 block mb-1">Custom Logo URL</label>
                    <Input 
                      placeholder="Enter logo URL" 
                      value={formData.logoUrl} 
                      onChange={(e) => onFormDataChange({ logoUrl: e.target.value })} 
                      className="border-2 focus:border-blue-400"
                    />
                    <div className="text-xs text-gray-500 mt-1">
                      Use a transparent PNG for best results
                    </div>
                  </>
                )}
              </Col>
              <Col span={8}>
                <label className="text-sm font-medium text-gray-700 block mb-1">Logo Size (% of QR)</label>
                <Input
                  type="number"
                  min={5}
                  max={35}
                  step={1}
                  value={formData.logoSizePercent}
                  onChange={(e) => onFormDataChange({ logoSizePercent: Number(e.target.value) })}
                  className="border-2 focus:border-blue-400"
                />
                <div className="text-xs text-gray-500 mt-1">
                  Recommended: 15-25% (lower for complex URLs)
                </div>
              </Col>
              <Col span={8}>
                <label className="text-sm font-medium text-gray-700 block mb-1">Logo Preview</label>
                <div className="border rounded p-2 flex items-center justify-center" style={{ height: '100px', background: '#f5f5f5' }}>
                  {formData.useDefaultLogo ? (
                    <img 
                      src={OLLogo} 
                      alt="Default Logo" 
                      style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }} 
                    />
                  ) : formData.logoUrl ? (
                    <img 
                      src={formData.logoUrl} 
                      alt="Custom Logo" 
                      style={{ maxHeight: '80px', maxWidth: '100%', objectFit: 'contain' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+TG9nbyBFcnJvcjwvdGV4dD48L3N2Zz4='
                      }}
                    />
                  ) : (
                    <div className="text-gray-400 text-center">No logo selected</div>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Actual size: {Math.round((formData.logoSizePercent / 100) * formData.qrWidth)}px ({formData.logoSizePercent}% of QR)
                </div>
              </Col>
            </Row>
          </div>

          {/* UTM Parameters */}
          <div>
            <h3 className="text-md font-medium mb-3 text-gray-700">UTM Parameters</h3>
            <Row gutter={16}>
              <Col span={8}>
                <label className="text-sm font-medium text-gray-700 block mb-1">UTM Source</label>
                <Input 
                  placeholder="E.g. facebook" 
                  value={formData.utmSource} 
                  onChange={(e) => onFormDataChange({ utmSource: e.target.value })} 
                  className="border-2 focus:border-blue-400"
                />
              </Col>
              <Col span={8}>
                <label className="text-sm font-medium text-gray-700 block mb-1">UTM Medium</label>
                <Input 
                  placeholder="E.g. social" 
                  value={formData.utmMedium} 
                  onChange={(e) => onFormDataChange({ utmMedium: e.target.value })} 
                  className="border-2 focus:border-blue-400"
                />
              </Col>
              <Col span={8}>
                <label className="text-sm font-medium text-gray-700 block mb-1">UTM Campaign</label>
                <Input 
                  placeholder="E.g. spring_sale" 
                  value={formData.utmCampaign} 
                  onChange={(e) => onFormDataChange({ utmCampaign: e.target.value })} 
                  className="border-2 focus:border-blue-400"
                />
              </Col>
            </Row>
          </div>

          {/* Generate Button */}
          <Button
            loading={state.generating || state.shorteningUrls}
            type="primary"
            className="font-semibold bg-green-500 hover:bg-green-600 mt-4"
            onClick={onGenerateQRCodes}
            size="large"
          >
            {state.shorteningUrls ? 'Shortening URLs...' : 'Generate Print-Quality QR Codes'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default QrCodeOptions 